import api from './client';
import { Timestamp } from 'firebase/firestore';
import type { SyncOperation } from 'types/sync_operation';

function toTimestamp(value: any): Timestamp {
  if (!value) return Timestamp.now();
  if (value instanceof Timestamp) return value;
  if (typeof value === 'string') {
    return Timestamp.fromDate(new Date(value));
  }
  if (value.seconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  return Timestamp.now();
}

function apiResponseToSyncOperation(response: any): SyncOperation {
  return {
    ...response,
    created_at: toTimestamp(response.created_at),
    completed_at: response.completed_at ? toTimestamp(response.completed_at) : undefined,
  };
}

export const syncOperationsApi = {
  list: async (params?: {
    org_id?: string;
    user_id?: string;
    status?: string;
    resource?: string;
    limit?: number;
  }): Promise<SyncOperation[]> => {
    const { data } = await api.get('/sync-operations', { params });
    return (data as any[]).map(apiResponseToSyncOperation);
  },

  get: async (operationId: string): Promise<SyncOperation> => {
    const { data } = await api.get(`/sync-operations/${operationId}`);
    return apiResponseToSyncOperation(data);
  },

  create: async (operation: Omit<SyncOperation, 'id' | 'created_at' | 'completed_at'>): Promise<SyncOperation> => {
    const { data } = await api.post('/sync-operations', operation);
    return apiResponseToSyncOperation(data);
  },

  update: async (operationId: string, updates: Partial<SyncOperation>): Promise<SyncOperation> => {
    const { data } = await api.patch(`/sync-operations/${operationId}`, updates);
    return apiResponseToSyncOperation(data);
  },

  delete: async (operationId: string): Promise<void> => {
    await api.delete(`/sync-operations/${operationId}`);
  },

  clearCompleted: async (orgId: string): Promise<{ message: string }> => {
    const { data } = await api.delete('/sync-operations', { params: { org_id: orgId } });
    return data;
  },
};
